import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Search, ArrowLeft, BookOpen, GraduationCap, ChevronRight, Download, Sparkles, Star, Heart } from 'lucide-react';
import { APP_CONFIG, PDF_URL } from '../constants/appConfig';
import { Input } from './ui/input';
import MarshallAudioPlayer from './MarshallAudioPlayer';
import GrantLavaLampOrbs from './GrantLavaLampOrbs';
import FamilyValuesSong from './FamilyValuesSong';
import PageMessaging from './shared/PageMessaging';
import BeautifulWordsBuilder from './BeautifulWordsBuilder';
import FeelingsNeedsHelper from './FeelingsNeedsHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function TeachersGuide() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showWordsBuilder, setShowWordsBuilder] = useState(false);
  const [isHelperOpen, setIsHelperOpen] = useState(false);
  const [featuredHighlights, setFeaturedHighlights] = useState([]);
  const [highlights, setHighlights] = useState(() => {
    const saved = localStorage.getItem('teacherGuideHighlights');
    return saved ? JSON.parse(saved) : [];
  });
  const [expandedChapters, setExpandedChapters] = useState(() => {
    const saved = localStorage.getItem('expandedChapters');
    return saved ? JSON.parse(saved) : [];
  });

  // Fetch featured highlights from backend
  useEffect(() => {
    const fetchFeaturedHighlights = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/ebook/highlights`);
        if (response.ok) {
          const data = await response.json();
          setFeaturedHighlights(data);
        }
      } catch (error) {
        console.error('Error fetching featured highlights:', error);
      }
    };
    fetchFeaturedHighlights();
  }, []);

  const toggleChapter = (chapterNum) => {
    setExpandedChapters(prev => {
      const updated = prev.includes(chapterNum) 
        ? prev.filter(n => n !== chapterNum)
        : [...prev, chapterNum];
      localStorage.setItem('expandedChapters', JSON.stringify(updated));
      return updated;
    });
  };

  // Toggle highlight for an item
  const toggleHighlight = (page, e) => {
    e.stopPropagation();
    setHighlights(prev => {
      const newHighlights = prev.includes(page)
        ? prev.filter(p => p !== page)
        : [...prev, page];
      localStorage.setItem('teacherGuideHighlights', JSON.stringify(newHighlights));
      return newHighlights;
    });
  };

  // Reset all highlights
  const resetHighlights = () => {
    setHighlights([]);
    localStorage.removeItem('teacherGuideHighlights');
  };

  // Check if item is highlighted
  const isHighlighted = (page) => highlights.includes(page);

  // Reference materials and tools (before workbook section)
  const referenceMaterials = [
    { page: 1, title: 'Title Page & Purpose of NVC', link: '/workbook/wellness-in-relationships', note: 'Cover' },
    { page: 5, title: 'Feelings & Needs Exercise', link: '/workbook/feelings-needs-exercise' },
    { page: 6, title: 'Feelings List', link: '/workbook/feelings-needs-exercise?section=feelings' },
    { page: 7, title: 'Body Sensations', link: '/workbook/body-sensations' },
    { page: 8, title: 'Your Presence is the Most Precious Gift', link: '/workbook/your-presence' },
    { page: 9, title: 'The Golden Rule', link: '/workbook/golden-rule' },
    { page: 10, title: 'The NVC Tree of Life', link: '/workbook/nvc-tree-of-life' },
    { page: 11, title: 'NVC Dance Floors (one person)', link: '/workbook/dance-floors-one' },
    { page: 12, title: 'NVC Dance Floors (two people)', link: '/workbook/dance-floors-two' },
    { page: 13, title: 'A Compassionate Communication Dialog: Slow it down, Chunk it down', link: '/workbook/compassionate-dialog' },
    { page: 14, title: 'Five Key Practices for a Compassionate Conversation', link: '/workbook/five-key-practices' },
    { page: 15, title: 'Basic Feelings and Needs', link: '/ofnr-trainer?tab=3' },
    { page: 16, title: 'Feelings & Needs Worksheet (Interactive Table)', link: '/workbook/basic-feelings-needs' },
    { page: 17, title: 'NVC: Statements and Questions (OFNR)', link: '/workbook/ofnr-statements-questions' },
    { page: 18, title: 'NVC OFNR Practice Sheets (p.18-24)', link: '/workbook/ofnr-practice-sheets' },
    { page: 25, title: 'Compassionate Communication Wheel', link: '/workbook/compassionate-wheel' },
    { page: 26, title: 'Worksheet for Finding Mutual Strategies', link: '/workbook/mutual-strategies' },
    { page: 27, title: 'Non-feeling Words', link: '/ofnr-trainer?tab=5' },
    { page: 29, title: 'Working with Conflict using NVC (Short Version)', link: '/workbook/conflict-short' },
    { page: 31, title: 'Working with Conflict using NVC (Long Version, p.31-36)', link: '/workbook/conflict-long' },
    { page: 37, title: 'Compassionate Communication Checklist', link: '/workbook/page-37' },
    { page: 38, title: 'Gratitude Exercises', link: '/workbook/gratitude-exercises' },
    { page: 40, title: 'Gratitude and Celebration Practices (NVC Style)', link: '/workbook/page-40' },
    { page: 43, title: 'Giving and Receiving Feedback NVC Style', link: '/workbook/giving-receiving-feedback' },
    { page: 45, title: 'Connection Roadmap', link: '/workbook/page-45' },
    { page: 46, title: 'Intimacy Building Questions', link: '/workbook/intimacy-questions' },
    { page: 48, title: 'The Three Areas of Attention', link: '/workbook/three-areas-attention' },
    { page: 49, title: 'The Interpersonal Dance of The 3 Areas of Attention', link: '/workbook/interpersonal-dance' },
    { page: 51, title: 'Our Internal Map of Reality', link: '/workbook/internal-map-reality' },
    { page: 52, title: 'I Made It Mean... Worksheet', link: '/workbook/i-made-it-mean' },
    { page: 53, title: 'Our Quadrune Brain', link: '/workbook/quadrune-brain' },
    { page: 54, title: 'The Quadrune Brain and Evolutionary Psychology', link: '/workbook/quadrune-evolution' },
    { page: 55, title: 'Take In the Good! (Positive Experiences)', link: '/workbook/page-55' },
    { page: 57, title: 'The Fear of Feeling Our Feelings: The Most Common Relationship Issue', link: '/workbook/page-57' },
    { page: 59, title: 'Risk the dreaded experience!', link: '/workbook/page-59' },
    { page: 61, title: 'Key Ideas and Assumptions of NVC', link: '/workbook/nvc-key-ideas' },
    { page: 63, title: 'Nonviolent Communication Primer', link: '/workbook/nvc-primer' },
    { page: 64, title: '  → The Components of Nonviolent Communication', link: '/workbook/nvc-components' },
    { page: 65, title: '  → 1. Observations', link: '/workbook/nvc-components' },
    { page: 66, title: '  → 2. Feelings', link: '/workbook/nvc-components' },
    { page: 67, title: '  → 3. Needs', link: '/workbook/nvc-components' },
    { page: 68, title: '  → 4. Requests', link: '/workbook/nvc-components' },
    { page: 69, title: '  → Empathy', link: '/workbook/nvc-components' },
    { page: 70, title: '  → Self-Empathy', link: '/workbook/self-empathy' },
    { page: 71, title: 'Compassionate Communication Suggestions', link: '/workbook/compassionate-suggestions' },
    { page: 72, title: 'COMPASSIONATE COMMUNICATION', link: '/workbook/compassionate-communication' },
    { page: 73, title: 'Lost Love & the Drive Towards Wholeness', link: '/workbook/lost-love' },
    { page: 75, title: 'When Your Past Shows Up in the Present', link: '/workbook/past-in-present' },
  ];

  // Workbook chapters (starting from page 77)
  // Featured item at top (not in a chapter)
  const homeworkPartnerSchedule = { 
    page: 78, 
    title: 'Weekly Homework Partner Schedule', 
    link: '/workbook/homework-partner-schedule', 
    featured: true 
  };
  
  const workbookChapters = [
    {
      number: 1,
      title: 'Communication that Blocks Compassion',
      page: 79,
      items: [
        { page: 79, title: 'Chapter 1 Presentation View', link: '/workbook/chapter-1', featured: true },
        { page: 79, title: 'Chapter 1 Introduction', link: '/workbook/chapter-1?section=0' },
        { page: 81, title: 'Our 4 Basic Choices of Response', link: '/workbook/chapter-1?section=3' },
        { page: 82, title: 'Four Basic Choices Exercise (p.82-86)', link: '/workbook/chapter-1?section=4' },
        { page: 87, title: 'Jackal Statements (OFNR Practice)', link: '/workbook/chapter-1?section=5' },
        { page: 88, title: 'Jackal Statements (Street Giraffe Practice)', link: '/workbook/chapter-1?section=6' },
        { page: 89, title: 'Using OFNR in Honesty and Empathy', link: '/workbook/chapter-1?section=7' },
        { page: 90, title: 'The Four D\'s of Disconnection', link: '/workbook/chapter-1?section=8' },
        { page: 92, title: 'Chapter 1 Possible Answers', link: '/workbook/chapter-1?section=9' },
        { page: 93, title: 'Chapter 1 Homework', link: '/workbook/chapter-1?section=10' },
      ]
    },
    {
      number: 2,
      title: 'Observations',
      page: 94,
      items: [
        { page: 94, title: 'Chapter 2 Introduction', link: '/workbook/chapter?chapter=2' },
        { page: 95, title: 'Exercise 1', link: '/workbook/chapter?chapter=2' },
        { page: 96, title: 'Exercise 2', link: '/workbook/chapter?chapter=2' },
        { page: 97, title: 'Chapter 2 Possible Answers', link: '/workbook/chapter?chapter=2' },
        { page: 98, title: 'Chapter 2 Homework', link: '/workbook/chapter?chapter=2' },
      ]
    },
    {
      number: 3,
      title: 'Feelings',
      page: 99,
      items: [
        { page: 99, title: 'Chapter 3 Introduction', link: '/workbook/chapter?chapter=3' },
        { page: 100, title: 'Feelings when needs ARE NOT satisfied', link: '/workbook/chapter?chapter=3' },
        { page: 101, title: 'Feelings when needs ARE satisfied', link: '/workbook/chapter?chapter=3' },
        { page: 102, title: 'Exercise 2 & 3', link: '/workbook/chapter?chapter=3' },
        { page: 103, title: 'Exercise 4', link: '/workbook/chapter?chapter=3' },
        { page: 104, title: 'Chapter 3 Possible Answers', link: '/workbook/chapter?chapter=3' },
        { page: 105, title: 'Alarm Feelings: Anger, Guilt, Shame, & Depression', link: '/workbook/chapter?chapter=3' },
        { page: 106, title: 'Acceptance, Clarity and Mourning', link: '/workbook/chapter?chapter=3' },
        { page: 107, title: 'Chapter 3 Homework', link: '/workbook/chapter?chapter=3' },
      ]
    },
    {
      number: 4,
      title: 'Needs',
      page: 108,
      items: [
        { page: 108, title: 'Chapter 4 Introduction', link: '/workbook/chapter?chapter=4' },
        { page: 109, title: 'Exercise 2', link: '/workbook/chapter?chapter=4' },
        { page: 110, title: 'Exercise 3', link: '/workbook/chapter?chapter=4' },
        { page: 111, title: 'Information in Celebration', link: '/workbook/chapter?chapter=4' },
        { page: 112, title: 'Chapter 4 Possible Answers', link: '/workbook/chapter?chapter=4' },
        { page: 113, title: 'Chapter 4 Homework', link: '/workbook/chapter?chapter=4' },
      ]
    },
    {
      number: 5,
      title: 'Requests',
      page: 114,
      items: [
        { page: 114, title: 'Chapter 5 Introduction', link: '/workbook/chapter?chapter=5' },
        { page: 115, title: 'Exercise 2', link: '/workbook/chapter?chapter=5' },
        { page: 116, title: 'Exercise 3', link: '/workbook/chapter?chapter=5' },
        { page: 117, title: 'Exercise 4', link: '/workbook/chapter?chapter=5' },
        { page: 118, title: '"Because I Care So Much" Cards', link: '/workbook/chapter?chapter=5' },
        { page: 119, title: 'What Keeps Me from Making a Request?', link: '/workbook/chapter?chapter=5' },
        { page: 120, title: 'COMMON FEARS AND BELIEFS', link: '/workbook/chapter?chapter=5' },
        { page: 121, title: '"Don\'t Criticize Me!" vs. Do-able requests (p.121-122)', link: '/workbook/chapter?chapter=5' },
        { page: 123, title: 'When Every Request is a Demand (p.123-124)', link: '/workbook/chapter?chapter=5' },
        { page: 125, title: 'Let\'s Make Our Life More Wonderful Together', link: '/workbook/chapter?chapter=5' },
        { page: 126, title: '101 Ideas for Offering and Asking (p.126-128)', link: '/workbook/chapter?chapter=5' },
        { page: 129, title: '101 Magic Words and Phrases (p.129-132)', link: '/workbook/chapter?chapter=5' },
        { page: 133, title: 'Chapter 5 Possible Answers', link: '/workbook/chapter?chapter=5' },
        { page: 134, title: 'Chapter 5 Homework', link: '/workbook/chapter?chapter=5' },
      ]
    },
    {
      number: 6,
      title: 'Empathy',
      page: 135,
      items: [
        { page: 135, title: 'Chapter 6 Introduction', link: '/workbook/chapter?chapter=6' },
        { page: 136, title: 'Exercise 2: Empathizing with Others', link: '/workbook/chapter?chapter=6' },
        { page: 137, title: 'Exercise 3', link: '/workbook/chapter?chapter=6' },
        { page: 138, title: 'Chapter 6 Possible Answers', link: '/workbook/chapter?chapter=6' },
        { page: 139, title: 'Guidelines for Giving Empathy (p.139-140)', link: '/workbook/chapter?chapter=6' },
        { page: 141, title: 'EMPATHY is... Empathy is not...', link: '/workbook/empathy-is-not' },
        { page: 142, title: 'Empathy Without Words (p.142-143)', link: '/workbook/chapter?chapter=6' },
        { page: 144, title: 'The Power of Silent Empathy When Words Don\'t Work', link: '/workbook/chapter?chapter=6' },
        { page: 145, title: 'Self-Empathy Fundamentals (p.145-146)', link: '/workbook/self-empathy' },
        { page: 147, title: 'Chapter 6 Homework', link: '/workbook/chapter?chapter=6' },
      ]
    },
    {
      number: 7,
      title: 'Working with Anger',
      page: 148,
      items: [
        { page: 148, title: 'Chapter 7 Introduction (p.148-149)', link: '/workbook/chapter?chapter=7' },
        { page: 150, title: 'Exercise 2', link: '/workbook/chapter?chapter=7' },
        { page: 151, title: 'Exercise 3', link: '/workbook/chapter?chapter=7' },
        { page: 152, title: 'From Anger to Transformation (p.152-153)', link: '/workbook/chapter?chapter=7' },
        { page: 154, title: 'Chapter 7 Homework', link: '/workbook/chapter?chapter=7' },
      ]
    },
    {
      number: 8,
      title: 'Practical Applications of NVC',
      page: 155,
      items: [
        { page: 155, title: 'Chapter 8 Introduction', link: '/workbook/chapter?chapter=8' },
        { page: 156, title: 'Translating self-judgment to self-empathy (p.156-157)', link: '/workbook/chapter?chapter=8' },
        { page: 158, title: 'Translating judgments of others (p.158-159)', link: '/workbook/chapter?chapter=8' },
        { page: 160, title: 'Expressing Gratitude & Appreciation (p.160-161)', link: '/workbook/chapter?chapter=8' },
        { page: 162, title: 'How to say "no" in giraffe', link: '/workbook/chapter?chapter=8' },
        { page: 163, title: 'Exercise (p.163-164)', link: '/workbook/chapter?chapter=8' },
        { page: 165, title: 'Responding Empathetically to a Jackal "No" (p.165-166)', link: '/workbook/chapter?chapter=8' },
        { page: 167, title: 'Possible answers', link: '/workbook/chapter?chapter=8' },
        { page: 168, title: 'Mourning Process with Self Only (p.168-169)', link: '/workbook/chapter?chapter=8' },
        { page: 170, title: 'NVC Mourning Process with Self and Others (p.170-172)', link: '/workbook/chapter?chapter=8' },
        { page: 173, title: 'Receiving Compliments (p.173-174)', link: '/workbook/chapter?chapter=8' },
        { page: 175, title: 'Sounding Natural (p.175-176)', link: '/workbook/chapter?chapter=8' },
        { page: 177, title: 'NEEDS: Universal Needs vs. Strategies', link: '/workbook/chapter?chapter=8' },
        { page: 178, title: 'Street Giraffe (p.178-179)', link: '/workbook/chapter?chapter=8' },
        { page: 180, title: 'Colloquial Options for speaking NVC (p.180-181)', link: '/workbook/chapter?chapter=8' },
        { page: 182, title: 'Chapter 8 Homework', link: '/workbook/chapter?chapter=8' },
        { page: 183, title: '30 Daily Practice Ideas for Living Gratefully (p.183-185)', link: '/workbook/chapter?chapter=8' },
        { page: 187, title: 'NVC Basics Participant\'s Evaluation', link: '/workbook/chapter?chapter=8' },
      ]
    },
  ];

  // Create a lookup map for page -> link
  const getPageLink = (page) => {
    // First check reference materials
    const refItem = referenceMaterials.find(item => item.page === page);
    if (refItem) return refItem.link;
    
    // Check workbook chapters
    for (const chapter of workbookChapters) {
      const chapterItem = chapter.items.find(item => item.page === page);
      if (chapterItem) return chapterItem.link;
    }
    
    // Fallback for unknown pages
    return `/workbook/page-${page}`;
  };

  // Filter function
  const filterItems = (items) => {
    if (!searchTerm) return items;
    return items.filter(item =>
      item.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filterChapters = (chapters) => {
    if (!searchTerm) return chapters;
    return chapters.map(chapter => ({
      ...chapter,
      items: chapter.items.filter(item =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        chapter.title.toLowerCase().includes(searchTerm.toLowerCase())
      )
    })).filter(chapter => chapter.items.length > 0 || chapter.title.toLowerCase().includes(searchTerm.toLowerCase()));
  };

  const filteredReferences = filterItems(referenceMaterials);
  const filteredChapters = filterChapters(workbookChapters);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-1" data-testid="belonging-ebook-title">The Belonging eBook</h1>
              <p className="text-slate-300 text-sm">Interactive Learning Companion</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Feelings & Needs Tool - Persistent */}
              <Button
                onClick={() => setIsHelperOpen(true)}
                className="bg-rose-600/80 hover:bg-rose-500 text-white text-sm px-3 py-2"
                data-testid="feelings-needs-tool-btn"
              >
                <Heart className="h-4 w-4 mr-1" />
                Feelings & Needs
              </Button>
              {/* Download PDF - Small, administrative */}
              <a
                href={PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-2 bg-slate-700/80 hover:bg-slate-600 text-white text-sm rounded-md transition-colors"
                data-testid="download-pdf-btn"
              >
                <Download className="h-4 w-4" />
                PDF
              </a>
              <Button
                onClick={() => navigate('/workbook/homework-partner-schedule')}
                variant="outline"
                className="border-purple-600 hover:border-purple-500 text-white hover:bg-purple-900/20 text-sm px-3 py-2"
              >
                📅 Schedule
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800 text-sm px-3 py-2"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Dashboard
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search for any topic, chapter, or exercise..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-400 focus:border-slate-500"
            />
          </div>
        </div>
      </div>

      {/* Today's Focus - NOT sticky, very compact */}
      <div className="container mx-auto px-6 py-2">
        <div className="flex flex-wrap items-center gap-3">
          {/* Featured Highlights - Compact inline pills */}
          {featuredHighlights.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="featured-highlights-section">
              <span className="flex items-center gap-1 text-amber-400 text-xs font-medium">
                <Star className="h-3 w-3" />
                Today:
              </span>
              {featuredHighlights.map((highlight) => (
                <button
                  key={highlight._id}
                  onClick={() => {
                    const link = getPageLink(highlight.page);
                    navigate(link);
                  }}
                  className="inline-flex items-center gap-1.5 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 rounded-full px-2.5 py-1 text-xs text-amber-200 hover:text-amber-100 transition-all"
                  data-testid={`featured-${highlight._id}`}
                >
                  <span className="font-bold text-amber-300">p.{highlight.page}</span>
                  <span className="truncate max-w-[120px]">{highlight.title}</span>
                </button>
              ))}
            </div>
          )}

          {/* Reset Highlights Button - inline */}
          {highlights.length > 0 && (
            <Button
              onClick={resetHighlights}
              variant="ghost"
              size="sm"
              className="text-amber-400 hover:text-amber-300 hover:bg-amber-900/20 text-xs px-2 py-1 h-auto"
            >
              Reset ({highlights.length})
            </Button>
          )}
        </div>
      </div>

      {/* Marshall Rosenberg Audio */}
      <div className="container mx-auto px-6 py-2">
        <MarshallAudioPlayer compact={true} />
      </div>

      {/* Two Column Layout */}
      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column: Reference Materials */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="h-5 w-5 text-slate-400" />
              <h2 className="text-xl font-bold text-white">Reference Materials & Tools</h2>
            </div>
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg overflow-hidden">
              <div className="divide-y divide-slate-700/30">
                {filteredReferences.map((item, idx) => (
                  <button
                    key={idx}
                    onClick={() => navigate(item.link)}
                    className={`w-full px-5 py-3 flex items-center justify-between hover:bg-slate-700/40 transition-colors group text-left relative ${
                      isHighlighted(item.page) ? 'bg-amber-900/20 border-l-4 border-amber-500' : ''
                    }`}
                    data-testid={`ref-item-${idx}`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <span className="text-slate-400 font-mono text-xs min-w-[2.5rem]">
                        p.{item.page}
                      </span>
                      <span className={`text-slate-100 group-hover:text-white transition-colors text-sm ${
                        isHighlighted(item.page) ? 'font-medium' : ''
                      }`}>
                        {item.title}
                      </span>
                      {item.incomplete && (
                        <span className="text-white text-xs" title="Content coming soon">○</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => toggleHighlight(item.page, e)}
                        className={`p-1.5 rounded transition-colors ${
                          isHighlighted(item.page)
                            ? 'bg-amber-600 text-white'
                            : 'bg-slate-700/50 text-slate-400 hover:bg-slate-600 hover:text-slate-300'
                        }`}
                        title={isHighlighted(item.page) ? 'Remove highlight' : 'Highlight for this class'}
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                          <path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <div className="text-slate-500 group-hover:text-slate-300 transition-colors text-sm">
                        →
                      </div>
                    </div>
                  </button>
                ))}
                {filteredReferences.length === 0 && (
                  <div className="px-5 py-8 text-center">
                    <p className="text-slate-400 text-sm">No reference materials found</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Workbook Chapters */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <GraduationCap className="h-5 w-5 text-slate-400" />
              <h2 className="text-xl font-bold text-white">Workbook Chapters</h2>
            </div>
            <div className="space-y-4">
              {/* Homework Partner Schedule - Featured at Top */}
              <a
                href={homeworkPartnerSchedule.link}
                className="block bg-gradient-to-r from-amber-900/40 to-slate-800/40 border-2 border-amber-600/50 rounded-lg px-5 py-4 hover:border-amber-500 transition-colors group"
                data-testid="homework-partner-schedule"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">📋</span>
                    <div>
                      <h3 className="text-amber-400 font-bold group-hover:text-amber-300">
                        {homeworkPartnerSchedule.title}
                      </h3>
                      <p className="text-slate-400 text-xs mt-1">Page {homeworkPartnerSchedule.page} • Student Worksheet</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-amber-500 group-hover:translate-x-1 transition-transform" />
                </div>
              </a>

              {filteredChapters.map((chapter) => (
                <div
                  key={chapter.number}
                  className="bg-slate-800/40 border border-slate-700/50 rounded-lg overflow-hidden"
                  data-testid={`chapter-${chapter.number}`}
                >
                  {/* Chapter Header */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleChapter(chapter.number);
                    }}
                    className={`w-full px-5 py-4 bg-slate-800/60 hover:bg-slate-700/60 transition-colors text-left group border-b border-slate-700/30 relative ${
                      isHighlighted(chapter.page) ? 'bg-amber-900/20 border-l-4 border-amber-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="text-2xl transform transition-transform" style={{ transform: expandedChapters.includes(chapter.number) ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                          ▶
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={`text-white font-bold text-lg group-hover:text-slate-100 truncate ${
                            isHighlighted(chapter.page) ? 'text-amber-200' : ''
                          }`}>
                            Ch. {chapter.number}: {chapter.title}
                          </h3>
                          <p className="text-slate-500 text-xs">p.{chapter.page}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => toggleHighlight(chapter.page, e)}
                          className={`p-1.5 rounded transition-colors ${
                            isHighlighted(chapter.page)
                              ? 'bg-amber-600 text-white'
                              : 'bg-slate-700/50 text-slate-400 hover:bg-slate-600 hover:text-slate-300'
                          }`}
                          title={isHighlighted(chapter.page) ? 'Remove highlight' : 'Highlight for this class'}
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                            <path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <div className="text-slate-500 group-hover:text-slate-300 transition-colors">
                          →
                        </div>
                      </div>
                    </div>
                  </button>

                  {/* Chapter Items - Only show if expanded */}
                  {expandedChapters.includes(chapter.number) && (
                    <div className="divide-y divide-slate-700/30">
                    {chapter.items.map((item, idx) => (
                      <button
                        key={idx}
                        onClick={() => navigate(item.link)}
                        className={`w-full px-5 py-2.5 flex items-center justify-between hover:bg-slate-700/40 transition-colors group text-left relative ${
                          isHighlighted(item.page) ? 'bg-amber-900/20 border-l-4 border-amber-500' : ''
                        } ${item.featured ? 'bg-gradient-to-r from-teal-900/30 to-emerald-900/20 border-l-4 border-teal-500' : ''}`}
                        data-testid={`chapter-${chapter.number}-item-${idx}`}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
                          <span className="text-slate-500 font-mono text-xs flex-shrink-0">
                            p.{item.page}
                          </span>
                          <span className={`text-slate-200 group-hover:text-white transition-colors text-sm truncate ${
                            isHighlighted(item.page) ? 'font-medium' : ''
                          } ${item.featured ? 'font-semibold text-teal-200' : ''}`}>
                            {item.featured && '⭐ '}{item.title}
                          </span>
                          {item.featured && (
                            <span className="bg-teal-600 text-white text-xs px-2 py-0.5 rounded-full flex-shrink-0">Presentation</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => toggleHighlight(item.page, e)}
                            className={`p-1.5 rounded transition-colors ${
                              isHighlighted(item.page)
                                ? 'bg-amber-600 text-white'
                                : 'bg-slate-700/50 text-slate-400 hover:bg-slate-600 hover:text-slate-300'
                            }`}
                            title={isHighlighted(item.page) ? 'Remove highlight' : 'Highlight for this class'}
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                              <path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" />
                            </svg>
                          </button>
                          <div className="text-slate-600 group-hover:text-slate-400 transition-colors text-sm">
                            →
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                  )}
                </div>
              ))}
              {filteredChapters.length === 0 && (
                <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-8 text-center">
                  <p className="text-slate-400 text-sm">No chapters found</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Deep Dive: Say What You Mean - Bottom of Content */}
        <div className="container mx-auto px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-amber-900/30 to-slate-800/50 border border-amber-700/40 rounded-2xl p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg">
                    <span className="text-white text-2xl">📖</span>
                  </div>
                  <div>
                    <h3 className="text-amber-300 font-bold text-lg">Deep Dive: Say What You Mean</h3>
                    <p className="text-slate-400 text-sm">Three Steps to Effective Conversation • By Oren Jay Sofer</p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/workbook/say-what-you-mean')}
                  className="bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white px-6"
                >
                  <ChevronRight className="h-4 w-4 mr-2" />
                  Explore Deep Dive
                </Button>
              </div>
              <div className="mt-5 grid grid-cols-3 gap-3">
                <div className="bg-blue-500/15 border border-blue-500/30 rounded-lg px-4 py-3 text-center">
                  <span className="text-blue-300 font-semibold">1. Lead with Presence</span>
                </div>
                <div className="bg-rose-500/15 border border-rose-500/30 rounded-lg px-4 py-3 text-center">
                  <span className="text-rose-300 font-semibold">2. Curiosity & Care</span>
                </div>
                <div className="bg-emerald-500/15 border border-emerald-500/30 rounded-lg px-4 py-3 text-center">
                  <span className="text-emerald-300 font-semibold">3. Focus on What Matters</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Family Values Song - Interactive Experience */}
        <div className="container mx-auto px-6">
          <FamilyValuesSong />
        </div>

        {/* Beautiful Closing Image with Orbs */}
        <div className="container mx-auto px-6 py-12 relative">
          <div className="max-w-4xl mx-auto relative">
            {/* Grant's Spiral Orbs Behind */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 0 }}>
              <div className="w-full h-full flex items-center justify-center opacity-60">
                <GrantLavaLampOrbs width={800} height={600} />
              </div>
            </div>
            
            {/* Image with Transparency */}
            <div className="relative" style={{ zIndex: 10 }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_belonging-sessions-1/artifacts/9w824o5h_ChatGPT%20Image%20Nov%2015%2C%202025%20at%2009_17_33%20PM.png"
                alt="The Belonging Sessions"
                className="w-full h-auto rounded-lg shadow-2xl"
                style={{ opacity: 0.9 }}
              />
            </div>
          </div>
        </div>

        {/* Bell Hooks Quote - Full quote at bottom of content */}
        <div className="container mx-auto px-6 py-8" data-testid="bell-hooks-quote">
          <div className="max-w-3xl mx-auto">
            <div className="relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-amber-500/20 rounded-xl p-8 shadow-lg">
              <div className="absolute top-4 left-6 text-4xl text-amber-500/30 font-serif">"</div>
              <blockquote className="relative z-10 text-center text-white/90 leading-relaxed text-base font-light tracking-wide px-8 pt-4">
                {APP_CONFIG.bellHooksQuote.text}
              </blockquote>
              <div className="mt-6 flex items-center justify-center gap-3">
                <div className="h-px w-12 bg-gradient-to-r from-transparent via-amber-500/50 to-transparent"></div>
                <p className="text-amber-400 font-medium text-sm">{APP_CONFIG.bellHooksQuote.author}</p>
                <div className="h-px w-12 bg-gradient-to-r from-transparent via-amber-500/50 to-transparent"></div>
              </div>
              <p className="text-amber-300/60 text-center text-xs mt-2 italic">{APP_CONFIG.bellHooksQuote.source}</p>
            </div>
          </div>
        </div>

        {/* Contact Form Section */}
        <div className="container mx-auto px-6 py-12">
          <div className="max-w-2xl mx-auto">
            <PageMessaging pageTitle="The Belonging eBook" pageNumber={0} />
          </div>
        </div>

        {/* Professional Footer */}
        <footer className="border-t border-slate-700/50 bg-slate-900/90 py-8">
          <div className="container mx-auto px-6">
            <div className="text-center text-slate-400 text-sm space-y-2">
              <p className="text-slate-300 font-semibold">© 2025 The Belonging Sessions with Steve Torma</p>
              <p>Building Compassionate Connection • Defiance Springs, Viroqua, USA</p>
              <p>Powered by a PlanetaryFam</p>
              <div className="flex justify-center gap-6 mt-4">
                <a href="/" className="hover:text-white transition-colors">Home</a>
                <a href="/lore" className="hover:text-white transition-colors">LORE</a>
                <a href="/belonging-ebook" className="hover:text-white transition-colors">Belonging eBook</a>
                <a href="/ofnr-trainer" className="hover:text-white transition-colors">OFNR Trainer</a>
                <a href="mailto:hello@defiancesprings.com" className="hover:text-white transition-colors">Contact</a>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Feelings & Needs Helper Popup */}
      <FeelingsNeedsHelper 
        isOpen={isHelperOpen} 
        onClose={() => setIsHelperOpen(false)} 
      />
    </div>
  );
}
